import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';

/**
 * «Копировать в…» и «Переместить в…» для блока, раздела и урока
 * (договор `docs/CONTRACTS/constructor-v2.md` §2).
 *
 * Здесь только то, что можно проверить без экрана: разбор отказов, списки для
 * выбора места и решение «можно ли переносить прямо сейчас».
 */

export type TransferKind = 'block' | 'section' | 'lesson';
export type TransferOp = 'copy' | 'move';

type T = (key: string, vars?: Record<string, unknown>) => string;

/**
 * Отказы ручек переноса (§2.5).
 *
 * ⚠️ РАЗЛИЧАТЬ ТОЛЬКО ПО `code`. `humanizeError` судит по статусу и на любой 409
 * говорит «такая запись уже существует» — а у переноса 409 значит две совсем
 * разные вещи: курс в подтипе и ответы учеников. Человеку нужно знать, какая
 * из них, потому что выход у них разный: скопировать вместо перемещения.
 */
const CODE_KEYS: Record<string, string> = {
  move_from_subtype: 'lessonBuilder.errMoveFromSubtype',
  block_has_answers: 'lessonBuilder.errBlockHasAnswers',
  section_has_answers: 'lessonBuilder.errSectionHasAnswers',
  lesson_has_answers: 'lessonBuilder.errLessonHasAnswers',
  target_not_found: 'lessonBuilder.errTargetNotFound',
  template_read_only: 'lessonBuilder.errTransferTemplate',
  block_not_found: 'lessonBuilder.errBlockNotFound',
  section_not_found: 'lessonBuilder.errSectionNotFound',
  lesson_not_found: 'lessonBuilder.errLessonNotFound',
  invalid_id: 'lessonBuilder.errTransferInvalid',
  invalid_body: 'lessonBuilder.errTransferInvalid',
};

export function transferErrorText(err: unknown, t: T): string {
  const code = apiErrorCode(err);
  const key = code ? CODE_KEYS[code] : undefined;
  return key ? t(key) : humanizeError(err);
}

export type Option = { value: string; label: string };

type ModuleLike = { id: string; title: string; lessons?: { id: string; title: string }[] | null };
type MarathonLike = { modules?: ModuleLike[] | null };
type SectionLike = { id: string; title: string; is_homework?: boolean };

/**
 * Уроки курса одним списком — так их показывает и страница курса.
 *
 * Модуль приписывается к названию, только когда модулей больше одного: в обычном
 * курсе модуль один и скрыт, и «Модуль 1 · Урок 3» был бы шумом.
 */
export function lessonOptions(marathon: MarathonLike | undefined): Option[] {
  const modules = marathon?.modules ?? [];
  const many = modules.length > 1;
  return modules.flatMap((m) =>
    (m.lessons ?? []).map((l) => ({ value: l.id, label: many ? `${m.title} · ${l.title}` : l.title })),
  );
}

export function moduleOptions(marathon: MarathonLike | undefined): Option[] {
  return (marathon?.modules ?? []).map((m) => ({ value: m.id, label: m.title }));
}

/** Разделы урока. Домашний помечается — у него своя роль у ученика. */
export function sectionOptions(sections: SectionLike[] | undefined, homeworkMark: string): Option[] {
  return (sections ?? []).map((s) => ({ value: s.id, label: s.is_homework ? `${s.title} · ${homeworkMark}` : s.title }));
}

/**
 * Чем запрещено перемещение ИЗ курса ещё до запроса.
 *
 * Сервер откажет и сам (423 и 409), но методист должен узнать это до выбора места,
 * а не после. Копировать из таких курсов можно всегда — источник только читается.
 */
export function moveLock(source: { isTemplate?: boolean; subtype?: string | null }): 'template' | 'subtype' | null {
  if (source.isTemplate) return 'template';
  if ((source.subtype ?? '').trim() !== '') return 'subtype';
  return null;
}

type DraftBlockLike = { id: string; serverId?: string };
type DraftSectionLike = { id: string; serverId?: string; blocks: DraftBlockLike[] };

/**
 * Можно ли переносить узел конструктора прямо сейчас — после сохранения.
 *
 * ⚠️ ПЕРЕНОСИТСЯ ТО, ЧТО ЛЕЖИТ НА СЕРВЕРЕ, а не то, что на экране. Если правка
 * блока не сохранилась, сервер перенесёт старую версию, а после перечитывания
 * урока правка пропадёт с экрана. Поэтому несохранённое не переносим вовсе.
 *
 * - `unsaved` — узла (или блока внутри раздела) ещё нет на сервере;
 * - `failed` — сервер отказал в записи этого блока при последнем сохранении.
 */
export function transferBlocker(
  kind: 'block' | 'section',
  id: string,
  sections: DraftSectionLike[],
  failures: Record<string, unknown>,
): 'unsaved' | 'failed' | 'missing' | null {
  if (kind === 'section') {
    const sec = sections.find((s) => s.id === id || s.serverId === id);
    if (!sec) return 'missing';
    if (!sec.serverId || sec.blocks.some((b) => !b.serverId)) return 'unsaved';
    if (sec.blocks.some((b) => failures[b.id])) return 'failed';
    return null;
  }
  for (const sec of sections) {
    const b = sec.blocks.find((x) => x.id === id || x.serverId === id);
    if (!b) continue;
    if (!b.serverId) return 'unsaved';
    if (failures[b.id]) return 'failed';
    return null;
  }
  return 'missing';
}

/**
 * Надо ли перечитать открытый урок после удачного переноса.
 *
 * Перемещение всегда уносит узел из открытого урока. Копия меняет его, только
 * если легла в него же. Во всех остальных случаях открытый урок на сервере не
 * изменился, и перечитывать его — значит сбить методисту выбранный раздел зря.
 */
export function needsReload(op: TransferOp, currentLessonId: string | undefined, targetLessonId: string | undefined): boolean {
  if (op === 'move') return true;
  return !!currentLessonId && currentLessonId === targetLessonId;
}
